import type {QueryClient} from '@tanstack/react-query'
import * as R from 'rambdax'
import {api, type TizraClient} from '../client'
import {logger} from '../log'
import {makeQueryParams, type TizraQueryArgs} from './params'

const log = logger('tizra/react-query')

/**
 * Attach the react-query client as window.tizra.api for general use, and flush
 * any queued calls.
 *
 * This is but also as a crutch for search config that previously made API calls
 * directly (which meant no benefit from the persistent client).
 */
export function attachWindowApi(queryClient: QueryClient) {
  if (typeof window !== 'undefined') {
    Object.assign<Window['tizra'], Partial<Window['tizra']>>(window.tizra, {
      api: makeWindowApi(queryClient),
    })
    flushQueuedApiCalls()
  }
}

function makeWindowApi(queryClient: QueryClient) {
  return R.fromPairs(
    R.keys(api).map(<K extends keyof TizraClient>(name: K) => [
      name,
      (...queryArgs: TizraQueryArgs<K, false>) => {
        const {enabled, ...params} = makeQueryParams<K, false>({
          name,
          api,
          infinite: false,
          queryArgs,
        })
        return queryClient.fetchQuery(params)
      },
    ]),
  ) as TizraClient
}

/**
 * Set up window.tizra.api to queue calls until the query client is attached.
 */
function setupApiQueue() {
  window.tizra ||= {} as Window['tizra']
  window.tizra._apiQueue ||= []
  window.tizra.api ||= new Proxy({} as TizraClient, {
    get(_, name) {
      if (typeof name !== 'string') return undefined
      return (...args: any[]) => {
        return new Promise<unknown>((resolve, reject) =>
          window.tizra._apiQueue!.push({name, args, resolve, reject}),
        )
      }
    },
  })
}

function flushQueuedApiCalls() {
  const apiQueue = window.tizra._apiQueue
  window.tizra._apiQueue = []
  if (apiQueue) {
    for (const {name, args, resolve, reject} of apiQueue) {
      if (!(name in window.tizra.api)) {
        reject(`invalid api: ${name}`)
        continue
      }
      try {
        window.tizra.api[name as 'query'](
          ...(args as Parameters<(typeof window.tizra.api)['query']>),
        ).then(resolve, reject)
      } catch (e) {
        reject(`${e}`)
      }
    }
  }
}

// This module sets up the queueing window.tizra.api every time it loads.
if (typeof window !== 'undefined') {
  setupApiQueue()
}
